import { useState } from 'react';
import { useParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { Field, Input, Select, Spinner, Text, makeStyles, tokens } from '@fluentui/react-components';
import { fetchExpenseSummary } from '../api/expenses';
import { fetchRanchos } from '../api/rancho.api';
import { fetchLotes } from '../api/lotes.api';
import { KpiCard } from '../components/shared/KpiCard';
import { DataTable, type DataTableColumn } from '../components/shared/DataTable';
import { useT } from '../i18n';
import { useIntlFormat } from '../hooks/useIntlFormat';
import { usePageTitle } from '../hooks/usePageTitle';
import { PageTitle } from '../components/shared/PageTitle';

const useStyles = makeStyles({
  root: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL, padding: tokens.spacingVerticalL },
  filters: { display: 'flex', gap: tokens.spacingHorizontalM, flexWrap: 'wrap', alignItems: 'flex-end' },
  kpiRow: { display: 'flex', gap: tokens.spacingHorizontalM, flexWrap: 'wrap' },
  split: { display: 'flex', gap: tokens.spacingHorizontalL, flexWrap: 'wrap', alignItems: 'flex-start' },
  col: { flex: '1', minWidth: '320px', display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS },
  muted: { color: tokens.colorNeutralForeground3 },
});

interface Row { key: string; label: string; total: number }

const monthStart = () => new Date().toISOString().slice(0, 8) + '01';
const today = () => new Date().toISOString().slice(0, 10);

/** B3 — dashboard de gastos generales. Solo se monta bajo `expenses:read`; el API vuelve a exigirlo. */
export function ExpensesDashboardPage() {
  const s = useStyles();
  const t = useT();
  const d = t.expensesDashboard;
  const { tenantId } = useParams<{ tenantId: string }>();
  const { fmtNum } = useIntlFormat();
  usePageTitle(d.title);

  const [from, setFrom] = useState(monthStart);
  const [to, setTo] = useState(today);
  const [ranchoId, setRanchoId] = useState('');
  const [loteId, setLoteId] = useState('');

  const { data: ranchos = [] } = useQuery({ queryKey: ['expDash-ranchos', tenantId], queryFn: fetchRanchos, enabled: !!tenantId });
  const { data: lotes = [] } = useQuery({
    queryKey: ['expDash-lotes', tenantId, ranchoId],
    queryFn: () => fetchLotes(ranchoId || null),
    enabled: !!tenantId,
  });
  const { data, isLoading, isError } = useQuery({
    queryKey: ['expenseSummary', tenantId, from, to, ranchoId, loteId],
    queryFn: () => fetchExpenseSummary(tenantId!, { from, to, ranchoId, loteId }),
    enabled: !!tenantId,
  });

  const money = (n: number) =>
    `${data?.baseCurrency ?? ''} ${fmtNum(n, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`.trim();

  const toRows = (m: Record<string, number>, sortByKey = false): Row[] =>
    Object.entries(m)
      .map(([key, total]) => ({ key, label: key, total }))
      .sort((a, b) => (sortByKey ? a.key.localeCompare(b.key) : b.total - a.total));

  const columns = (header: string): DataTableColumn<Row>[] => [
    { key: 'label', header, width: 2 },
    { key: 'total', header: d.colTotal, align: 'right', render: r => money(r.total) },
  ];
  const cpRows: Row[] = (data?.byCounterparty ?? []).map(c => ({
    key: c.counterpartyId ?? `n:${c.name}`, label: c.name || d.noCounterparty, total: c.total,
  }));

  return (
    <div className={s.root}>
      <div>
        <PageTitle>{d.title}</PageTitle>
        <Text className={s.muted} block>{d.subtitle}</Text>
      </div>

      <div className={s.filters}>
        <Field label={d.from}><Input type="date" value={from} onChange={(_, v) => setFrom(v.value)} /></Field>
        <Field label={d.to}><Input type="date" value={to} onChange={(_, v) => setTo(v.value)} /></Field>
        <Field label={d.rancho}>
          <Select value={ranchoId} onChange={(_, v) => { setRanchoId(v.value); setLoteId(''); }}>
            <option value="">{d.all}</option>
            {ranchos.map(r => <option key={r.ranchoId} value={r.ranchoId}>{r.name}</option>)}
          </Select>
        </Field>
        <Field label={d.lote}>
          <Select value={loteId} onChange={(_, v) => setLoteId(v.value)}>
            <option value="">{d.all}</option>
            {lotes.map(l => <option key={l.loteId} value={l.loteId}>{l.name}</option>)}
          </Select>
        </Field>
      </div>

      {isLoading && <Spinner size="small" label={d.loading} />}
      {isError && <Text role="alert" style={{ color: tokens.colorStatusDangerForeground1 }}>{d.error}</Text>}

      {data && (
        <>
          <div className={s.kpiRow}>
            <KpiCard label={d.kpiTotal} value={money(data.total)} />
            <KpiCard label={d.kpiCount} value={data.count} />
            <KpiCard label={d.kpiPending} value={data.pendingCount} sub={money(data.pendingTotal)} />
          </div>
          {data.excludedOtherBaseCurrency > 0 && (
            <Text className={s.muted}>{d.excluded.replace('{n}', String(data.excludedOtherBaseCurrency))}</Text>
          )}
          <div className={s.split}>
            <div className={s.col}>
              <Text weight="semibold">{d.byCategory}</Text>
              <DataTable columns={columns(d.colCategory)} rows={toRows(data.byCategory)} rowKey={r => r.key} emptyMessage={d.empty} />
            </div>
            <div className={s.col}>
              <Text weight="semibold">{d.byCounterparty}</Text>
              <DataTable columns={columns(d.colCounterparty)} rows={cpRows} rowKey={r => r.key} emptyMessage={d.empty} />
            </div>
            <div className={s.col}>
              <Text weight="semibold">{d.byMonth}</Text>
              <DataTable columns={columns(d.colMonth)} rows={toRows(data.byMonth, true)} rowKey={r => r.key} emptyMessage={d.empty} />
            </div>
          </div>
        </>
      )}
    </div>
  );
}
